import { Inject, Injectable, InjectionToken, NgZone, OnDestroy } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { BehaviorSubject, distinctUntilChanged, skip, Subject, Subscription } from 'rxjs'
import { fetchEventSource, FetchEventSourceInit } from '@microsoft/fetch-event-source'
import { SseEvent, SseEventType } from '@app/domain/SseEvent'
import { AuthService } from '@services/auth.service'
import { ConnectivityService } from '@services/connectivity.service'
import { TokenRepositoryService } from '@services/token-repository.service'
import { environment } from '@environments/environment'

export type FetchEventSourceFn = (url: RequestInfo, init: FetchEventSourceInit) => Promise<void>
export const FETCH_EVENT_SOURCE = new InjectionToken<FetchEventSourceFn>('FETCH_EVENT_SOURCE', {
    providedIn: 'root',
    factory: () => fetchEventSource,
})

export class RetryableError extends Error {}
class FatalError extends Error {}

@Injectable({
    providedIn: 'root',
})
export class SseService implements OnDestroy {

    private static readonly INITIAL_RECONNECT_DELAY_MS = 1000
    private static readonly MAX_RECONNECT_DELAY_MS = 30000

    private connected = new BehaviorSubject<boolean>(false)
    public connected$ = this.connected.asObservable()

    private events = new Subject<SseEvent>()
    public events$ = this.events.asObservable()

    private abortController: AbortController | null = null
    private currentBusinessId: string | null = null
    private currentVenueId: string | null = null
    private visibilityHandler: (() => void) | null = null
    private connectivitySubscription: Subscription | null = null
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null
    private reconnectDelayMs = SseService.INITIAL_RECONNECT_DELAY_MS
    private connectionInProgress = false
    private isReconnecting = false
    private pendingTokenRefreshReconnect = false

    constructor(
        private authService: AuthService,
        private connectivityService: ConnectivityService,
        private tokenRepositoryService: TokenRepositoryService,
        private zone: NgZone,
        @Inject(DOCUMENT) private document: Document,
        @Inject(FETCH_EVENT_SOURCE) private fetchEventSource: FetchEventSourceFn,
    ) {
        this.visibilityHandler = () => this.handleVisibilityChange()
        this.document.addEventListener('visibilitychange', this.visibilityHandler)
        this.connectivitySubscription = this.connectivityService.isOnline$
            .pipe(
                skip(1),
                distinctUntilChanged()
            )
            .subscribe(isOnline => {
                if (isOnline) {
                    this.handleOnline()
                    return
                }
                this.handleOffline()
            })
    }

    ngOnDestroy() {
        this.disconnect()
        this.connectivitySubscription?.unsubscribe()
        this.connectivitySubscription = null
        if (this.visibilityHandler) {
            this.document.removeEventListener('visibilitychange', this.visibilityHandler)
        }
    }

    connect(businessId: string, venueId: string) {
        const connectingToSameVenue = this.currentBusinessId === businessId && this.currentVenueId === venueId
        if (connectingToSameVenue && (this.connected.value || this.connectionInProgress || this.reconnectTimer !== null)) {
            return
        }
        this.resetReconnectDelay()
        this.disconnect()
        this.currentBusinessId = businessId
        this.currentVenueId = venueId
        this.startConnection()
    }

    disconnect() {
        this.clearReconnectTimer()
        if (this.abortController) {
            this.abortController.abort()
            this.abortController = null
        }
        this.connectionInProgress = false
        this.pendingTokenRefreshReconnect = false
        this.connected.next(false)
        this.currentBusinessId = null
        this.currentVenueId = null
    }

    private startConnection() {
        const businessId = this.currentBusinessId
        const venueId = this.currentVenueId
        if (!businessId || !venueId || !this.isOnline() || this.connectionInProgress) {
            return
        }

        this.clearReconnectTimer()
        this.connectionInProgress = true
        const url = new URL(`${environment.apiBaseURL}/organisation/business/${businessId}/venue/${venueId}/events`)
        url.searchParams.set('ngsw-bypass', 'true')
        this.abortController = new AbortController()

        this.zone.runOutsideAngular(() => {
            this.fetchEventSource(url.toString(), {
                headers: {
                    'Authorization': `Bearer ${this.tokenRepositoryService.getAccessToken()}`,
                },
                signal: this.abortController!.signal,
                openWhenHidden: true,
                onopen: async (response: Response) => {
                    if (response.ok) {
                        this.connectionInProgress = false
                        this.pendingTokenRefreshReconnect = false
                        this.resetReconnectDelay()
                        this.zone.run(() => this.connected.next(true))
                        return
                    }
                    if (response.status === 403) {
                        throw new FatalError('SSE not enabled for this venue')
                    }
                    if (response.status === 401) {
                        throw new RetryableError('Unauthorized')
                    }
                    throw new FatalError(`Unexpected status ${response.status}`)
                },
                onmessage: (event) => {
                    if (event.event === 'heartbeat' || event.event === 'connected') {
                        return
                    }
                    try {
                        const sseEvent: SseEvent = JSON.parse(event.data)
                        this.zone.run(() => {
                            this.events.next(sseEvent)
                        })
                    } catch {
                        // Ignore malformed events
                    }
                },
                onerror: (error) => {
                    this.connectionInProgress = false
                    this.zone.run(() => this.connected.next(false))
                    if (error instanceof FatalError) {
                        throw error
                    }
                    if (error instanceof RetryableError) {
                        this.pendingTokenRefreshReconnect = true
                        this.reconnectWithTokenRefresh()
                    } else if (this.isOnline()) {
                        this.scheduleReconnect()
                    } else {
                        this.abortController = null
                    }
                    throw new FatalError('Reconnecting')
                },
                onclose: () => {
                    this.connectionInProgress = false
                    this.zone.run(() => this.connected.next(false))
                },
            })
        })
    }

    private async reconnectWithTokenRefresh() {
        if (this.document.hidden || !this.isOnline()) {
            this.pendingTokenRefreshReconnect = true
            return
        }
        if (this.isReconnecting) {
            return
        }
        this.pendingTokenRefreshReconnect = false
        this.isReconnecting = true
        this.connectionInProgress = false

        if (this.abortController) {
            this.abortController.abort()
            this.abortController = null
        }

        const expectedBusinessId = this.currentBusinessId
        const expectedVenueId = this.currentVenueId

        try {
            if (expectedBusinessId && expectedVenueId) {
                await this.authService.refreshToken()
                if (
                    this.currentBusinessId === expectedBusinessId &&
                    this.currentVenueId === expectedVenueId &&
                    !this.abortController &&
                    this.isOnline()
                ) {
                    this.startConnection()
                }
            }
        } catch {
            // Token refresh failed - stop retrying to avoid request flood
        } finally {
            this.isReconnecting = false
        }
    }

    private reconnect() {
        if (this.connectionInProgress) {
            return
        }
        if (this.abortController) {
            this.abortController.abort()
            this.abortController = null
        }
        if (this.currentBusinessId && this.currentVenueId && this.isOnline()) {
            this.startConnection()
        }
    }

    private resumeReconnect() {
        if (this.pendingTokenRefreshReconnect) {
            void this.reconnectWithTokenRefresh()
            return
        }
        this.reconnect()
    }

    private handleVisibilityChange() {
        if (this.document.hidden) {
            this.clearReconnectTimer()
            return
        } else {
            if (!this.connected.value && !this.connectionInProgress && this.currentBusinessId && this.currentVenueId && this.isOnline()) {
                this.resumeReconnect()
            }
        }
    }

    private handleOnline() {
        if (!this.document.hidden && !this.connected.value && !this.connectionInProgress && this.currentBusinessId && this.currentVenueId) {
            this.clearReconnectTimer()
            this.resumeReconnect()
        }
    }

    private handleOffline() {
        this.clearReconnectTimer()
        this.connectionInProgress = false
        if (this.abortController) {
            this.abortController.abort()
            this.abortController = null
        }
        this.connected.next(false)
    }

    private scheduleReconnect() {
        if (this.reconnectTimer || this.document.hidden) {
            return
        }
        const delay = this.reconnectDelayMs
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, SseService.MAX_RECONNECT_DELAY_MS)
            this.reconnect()
        }, delay)
    }

    private clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }
    }

    private resetReconnectDelay() {
        this.reconnectDelayMs = SseService.INITIAL_RECONNECT_DELAY_MS
    }

    private isOnline(): boolean {
        return this.connectivityService.isOnline
    }
}
